const clamp = (value, min, max) => Math.min(max, Math.max(min, value));
const n = (value, fallback = 0) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};
const dist = (a, b) => Math.hypot(b.x - a.x, b.y - a.y);
const rp = (x, y) => ({ x: Number(x.toFixed(4)), y: Number(y.toFixed(4)) });
const toSVG = (point) => `${point.x} ${point.y}`;

const normalize = (vector, fallback = { x: 1, y: 0 }) => {
  const mag = Math.hypot(vector.x, vector.y);
  if (mag < 1e-8) return fallback;
  return { x: vector.x / mag, y: vector.y / mag };
};

const lerp = (a, b, t) => ({
  x: a.x + ((b.x - a.x) * t),
  y: a.y + ((b.y - a.y) * t),
});

const cubicPoint = (p0, p1, p2, p3, t) => {
  const inv = 1 - t;
  return {
    x: (inv ** 3 * p0.x) + (3 * inv * inv * t * p1.x) + (3 * inv * t * t * p2.x) + (t ** 3 * p3.x),
    y: (inv ** 3 * p0.y) + (3 * inv * inv * t * p1.y) + (3 * inv * t * t * p2.y) + (t ** 3 * p3.y),
  };
};

const sampleCubic = (p0, p1, p2, p3, steps = 48) => (
  Array.from({ length: steps + 1 }, (_, index) => cubicPoint(p0, p1, p2, p3, index / steps))
);

const polylineLength = (points = []) => points.slice(1).reduce(
  (sum, point, index) => sum + dist(points[index], point),
  0,
);

const cubicLength = (p0, p1, p2, p3, steps = 56) => polylineLength(sampleCubic(p0, p1, p2, p3, steps));

const cubicSplit = (p0, p1, p2, p3, tRaw) => {
  const t = clamp(tRaw, 0, 1);
  const a = lerp(p0, p1, t);
  const b = lerp(p1, p2, t);
  const c = lerp(p2, p3, t);
  const d = lerp(a, b, t);
  const e = lerp(b, c, t);
  const m = lerp(d, e, t);
  return {
    splitPoint: m,
    left: { p0, p1: a, p2: d, p3: m },
    right: { p0: m, p1: e, p2: c, p3 },
  };
};

const solveCubicTForArcFromStart = (p0, p1, p2, p3, targetDistance) => {
  const total = cubicLength(p0, p1, p2, p3, 64);
  const clamped = clamp(targetDistance, 0, total);
  if (clamped <= 1e-6) return 0;
  if (Math.abs(clamped - total) <= 1e-6) return 1;
  let lo = 0;
  let hi = 1;
  for (let index = 0; index < 28; index += 1) {
    const mid = (lo + hi) / 2;
    const split = cubicSplit(p0, p1, p2, p3, mid);
    const len = cubicLength(split.left.p0, split.left.p1, split.left.p2, split.left.p3, 40);
    if (len < clamped) lo = mid;
    else hi = mid;
  }
  return (lo + hi) / 2;
};

const rotateVec = (vector, angle) => ({
  x: (vector.x * Math.cos(angle)) - (vector.y * Math.sin(angle)),
  y: (vector.x * Math.sin(angle)) + (vector.y * Math.cos(angle)),
});

const pickToward = (axis, toward, angle) => {
  const unit = normalize(axis);
  const goal = normalize(toward);
  const left = rotateVec(unit, angle);
  const right = rotateVec(unit, -angle);
  return ((left.x * goal.x) + (left.y * goal.y)) >= ((right.x * goal.x) + (right.y * goal.y))
    ? left
    : right;
};

const LEAF_BACK_ANGLE = (89 * Math.PI) / 180;
const LEAF_POINT_ANGLE = (95 * Math.PI) / 180;

const cubicTangent = (p0, p1, p2, p3, t) => {
  const inv = 1 - t;
  return normalize({
    x: (3 * inv * inv * (p1.x - p0.x)) + (6 * inv * t * (p2.x - p1.x)) + (3 * t * t * (p3.x - p2.x)),
    y: (3 * inv * inv * (p1.y - p0.y)) + (6 * inv * t * (p2.y - p1.y)) + (3 * t * t * (p3.y - p2.y)),
  });
};

const reflectAcrossLine = (point, origin, tangent) => {
  const axis = normalize(tangent);
  const vx = point.x - origin.x;
  const vy = point.y - origin.y;
  const along = (vx * axis.x) + (vy * axis.y);
  const proj = { x: origin.x + (along * axis.x), y: origin.y + (along * axis.y) };
  return rp((2 * proj.x) - point.x, (2 * proj.y) - point.y);
};

const buildLeafTopHandles = (clb0, clp0, clp1, foldB1, startDx, startDy, endDx, endDy) => {
  const topChord = Math.max(0.4, dist(clb0, clp1));
  const topOut = pickToward(
    { x: foldB1.x - clb0.x, y: foldB1.y - clb0.y },
    { x: clp1.x - clb0.x, y: clp1.y - clb0.y },
    LEAF_BACK_ANGLE,
  );
  const topIn = pickToward(
    { x: clp0.x - clp1.x, y: clp0.y - clp1.y },
    { x: clb0.x - clp1.x, y: clb0.y - clp1.y },
    LEAF_POINT_ANGLE,
  );
  const startLen = topChord / 3;
  const endLen = topChord / 3;
  const startCtrlBase = rp(clb0.x + (topOut.x * startLen), clb0.y + (topOut.y * startLen));
  const endCtrlBase = rp(clp1.x + (topIn.x * endLen), clp1.y + (topIn.y * endLen));
  return {
    startCtrlBase,
    endCtrlBase,
    startCtrl: rp(startCtrlBase.x + n(startDx, 0), startCtrlBase.y + n(startDy, 0)),
    endCtrl: rp(endCtrlBase.x + n(endDx, 0), endCtrlBase.y + n(endDy, 0)),
  };
};

const rotateAround = (point, origin, cos, sin) => ({
  x: origin.x + ((point.x - origin.x) * cos) - ((point.y - origin.y) * sin),
  y: origin.y + ((point.x - origin.x) * sin) + ((point.y - origin.y) * cos),
});

export function mapCollarLayoutToBody(point, transform) {
  if (!point || !transform) return point;
  const pivot = transform.pivot || { x: 0, y: 0 };
  const shifted = {
    x: point.x - n(transform.shiftX, 0),
    y: point.y - n(transform.shiftY, 0),
  };
  const body = rotateAround(shifted, pivot, n(transform.cos, 1), -n(transform.sin, 0));
  return rp(body.x, body.y);
}

const buildLeafPath = (foldStart, clb0, topStartCtrl, topEndCtrl, clp1, clp0, foldLeft) => ([
  `M ${toSVG(foldStart)}`,
  `L ${toSVG(clb0)}`,
  `C ${toSVG(topStartCtrl)} ${toSVG(topEndCtrl)} ${toSVG(clp1)}`,
  `L ${toSVG(clp0)}`,
  `C ${toSVG(foldLeft.p2)} ${toSVG(foldLeft.p1)} ${toSVG(foldStart)}`,
  'Z',
].join(' '));

export function buildShirtCollarLeaf({
  layoutB0,
  layoutB1,
  layoutE1,
  layoutTopStartCtrl,
  layoutTopEndCtrl,
  leafBackHeight = 1.75,
  tieGap = 1,
  pointLength = 3,
  spread = 0,
  topStartCtrlDx = 0,
  topStartCtrlDy = 0,
  topEndCtrlDx = 0,
  topEndCtrlDy = 0,
  backInsetDx = 0,
} = {}) {
  if (!layoutB1 || !layoutE1 || !layoutTopStartCtrl || !layoutTopEndCtrl) return null;

  const backHeight = Math.max(0.5, n(leafBackHeight, 1.75));
  const maxInset = Math.max(0.05, backHeight - 0.05);
  const backInset = clamp(0.25 - n(backInsetDx, 0), -maxInset, maxInset);
  const length = Math.max(0.5, n(pointLength, 3));
  const rawSpread = n(spread, 0);
  const spreadX = clamp(rawSpread, -length + 0.05, length - 0.05);
  const upSign = layoutB0 && layoutB1.y > layoutB0.y ? 1 : -1;
  const backRise = Math.sqrt(Math.max(0.05, (backHeight * backHeight) - (backInset * backInset)));
  const ClB0 = rp(layoutB1.x - backInset, layoutB1.y + (upSign * backRise));

  const bandStart = layoutB1;
  const bandStartCtrl = layoutTopStartCtrl;
  const bandEndCtrl = layoutTopEndCtrl;
  const bandEnd = layoutE1;
  const bandLen = Math.max(1e-3, cubicLength(bandStart, bandStartCtrl, bandEndCtrl, bandEnd, 64));
  const fromE1 = clamp(n(tieGap, 1) / 2, 0.05, Math.max(0.06, bandLen - 0.08));
  const walkFromB1 = bandLen - fromE1;
  const splitT = solveCubicTForArcFromStart(bandStart, bandStartCtrl, bandEndCtrl, bandEnd, walkFromB1);
  const split = cubicSplit(bandStart, bandStartCtrl, bandEndCtrl, bandEnd, splitT);
  const ClP0 = rp(split.splitPoint.x, split.splitPoint.y);

  const rise = Math.sqrt(Math.max(0.05, (length * length) - (spreadX * spreadX)));
  const ClP1 = rp(ClP0.x + spreadX, ClP0.y + (upSign * rise));

  const topHandles = buildLeafTopHandles(
    ClB0,
    ClP0,
    ClP1,
    layoutB1,
    topStartCtrlDx,
    topStartCtrlDy,
    topEndCtrlDx,
    topEndCtrlDy,
  );
  const topStartCtrlBase = topHandles.startCtrlBase;
  const topEndCtrlBase = topHandles.endCtrlBase;
  const topStartCtrl = topHandles.startCtrl;
  const topEndCtrl = topHandles.endCtrl;

  const layoutPath = buildLeafPath(layoutB1, ClB0, topStartCtrl, topEndCtrl, ClP1, ClP0, split.left);

  const foldedClB0 = rp(layoutB1.x - backInset, layoutB1.y - (upSign * backRise));
  const sewTangentAtP0 = cubicTangent(bandStart, bandStartCtrl, bandEndCtrl, bandEnd, splitT);
  const foldedClP1 = reflectAcrossLine(ClP1, ClP0, sewTangentAtP0);
  const foldedTopStartCtrl = rp(topStartCtrl.x, (2 * layoutB1.y) - topStartCtrl.y);
  const foldedTopEndCtrl = reflectAcrossLine(topEndCtrl, ClP0, sewTangentAtP0);
  const foldedLayoutPath = buildLeafPath(
    layoutB1,
    foldedClB0,
    foldedTopStartCtrl,
    foldedTopEndCtrl,
    foldedClP1,
    ClP0,
    split.left,
  );

  return {
    layoutPath,
    foldedLayoutPath,
    layoutPoints: {
      ClB0,
      ClP0,
      ClP1,
    },
    foldedLayoutPoints: {
      ClB0: foldedClB0,
      ClP0,
      ClP1: foldedClP1,
    },
    foldedTopCurve: {
      start: foldedClB0,
      end: foldedClP1,
      startCtrl: foldedTopStartCtrl,
      endCtrl: foldedTopEndCtrl,
    },
    topCurve: {
      start: ClB0,
      end: ClP1,
      startCtrl: topStartCtrl,
      endCtrl: topEndCtrl,
      startCtrlBase: topStartCtrlBase,
      endCtrlBase: topEndCtrlBase,
    },
    foldStart: layoutB1,
    foldSplit: split.left,
    draftPoints: [ClB0, ClP0, ClP1, topStartCtrl, topEndCtrl, foldedClB0, foldedClP1],
    metrics: {
      collarLeafBackHeight: Number(backHeight.toFixed(4)),
      collarLeafTieGap: Number(n(tieGap, 1).toFixed(4)),
      collarLeafPointLength: Number(dist(ClP0, ClP1).toFixed(4)),
      collarLeafSpread: Number(spreadX.toFixed(4)),
      collarLeafBackInset: Number(backInset.toFixed(4)),
    },
  };
}

export function buildShirtCollarLeafOnBody(leaf, transform) {
  if (!leaf?.foldedLayoutPoints || !leaf.foldedTopCurve || !leaf.foldSplit || !transform) return null;
  const map = (point) => mapCollarLayoutToBody(point, transform);
  const points = {
    ClB0: map(leaf.foldedLayoutPoints.ClB0),
    ClP0: map(leaf.foldedLayoutPoints.ClP0),
    ClP1: map(leaf.foldedLayoutPoints.ClP1),
  };
  const foldStart = map(leaf.foldStart);
  const foldLeft = {
    p1: map(leaf.foldSplit.p1),
    p2: map(leaf.foldSplit.p2),
  };
  const topStartCtrl = map(leaf.foldedTopCurve.startCtrl);
  const topEndCtrl = map(leaf.foldedTopCurve.endCtrl);
  return {
    path: buildLeafPath(foldStart, points.ClB0, topStartCtrl, topEndCtrl, points.ClP1, points.ClP0, foldLeft),
    points,
    draftPoints: [foldStart, points.ClB0, points.ClP0, points.ClP1, topStartCtrl, topEndCtrl],
  };
}
